import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DotIcon from '@mui/icons-material/FiberManualRecord';
import LockIcon from '@mui/icons-material/Lock';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Card, Container, Grid, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useNavigate } from 'react-router';
import Button from '/@/components/button';
import colors from '/@/components/theme-provider/colors';
import { useFieldById } from '/@/hooks/fields';
import { useNSRecommendationById } from '/@/hooks/nitrogen-smart';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import { updateNSRecommendationFromAPI } from '/@/store/nitrogen-recommendation/apiProviders';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { formatDate } from '/@/utils';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';
import Chip from '../chip';

const DetailHeader = React.forwardRef(
    /** @param {{id: string}} props */
    (props, ref) => {
        const { id } = props;
        const { data: nitrogenRecommendation } = useNSRecommendationById(id);
        const isMobile = useIsMobile();
        const history = useNavigate();

        const status = {
            pending: {
                label: _('Pendiente'),
                color: 'disabled',
                mobileColor: colors.gray[200],
                component: <Chip label={_('PENDIENTE')} labelColor={colors.gray.default} />,
                tooltip: _('Un técnico especializado está creando tu recomendación. Te contactaremos pronto.'),
            },
            processing: {
                label: _('Procesando'),
                color: 'warning',
                mobileColor: colors.warning[200],
                component: (
                    <Chip
                        icon={<LockIcon />}
                        label={_('PROCESANDO')}
                        labelColor={colors.themes.dekalb.primary.default}
                        bgcolor={colors.warning[200]}
                        iconClasses={{
                            fontSize: 11,
                            color: colors.themes.dekalb.primary.default,
                        }}
                    />
                ),
                tooltip: _('Un técnico especializado está creando tu recomendación. Te contactaremos pronto.'),
            },
            available: {
                label: _('Disponible'),
                color: 'primary',
                mobileColor: colors.success[100],
                component: (
                    <Chip label={_('DISPONIBLE')} labelColor={colors.success[200]} bgcolor={colors.success[100]} />
                ),
                tooltip: '',
            },
        }[nitrogenRecommendation.status] || { label: 'Wrong status', color: '' };

        const fieldQuery = useFieldById(
            { id: nitrogenRecommendation?.field },
            { enabled: nitrogenRecommendation?.field }
        );
        const { open: showSnackbar, toggle: toggleSnackbar } = useToggle();

        return (
            <Box
                ref={ref}
                sx={{
                    borderTop: (t) => `1px solid ${t.palette.grey[200]}`,
                    borderBottom: (t) => `1px solid ${t.palette.grey[200]}`,
                    py: 2,
                    my: { xs: 0, md: 2 },
                }}
            >
                <Container maxWidth='lg'>
                    <Grid container spacing={2} alignItems='center'>
                        <Grid item container direction='column' sx={{ flexGrow: 1, gap: 2 }}>
                            {isMobile && <Grid item>{status.component}</Grid>}
                            <Grid item>
                                <Title bold>{fieldQuery.data?.name}</Title>
                            </Grid>
                            <Grid item>
                                <Title size='sm'>{_('Recomendación proyecto Nitrógeno Smart')}</Title>
                            </Grid>
                        </Grid>
                        {nitrogenRecommendation && (
                            <React.Fragment>
                                {nitrogenRecommendation.status === 'pending' && (
                                    <React.Fragment>
                                        <Grid
                                            item
                                            sx={{ width: { xs: '100%', md: 'auto' } }}
                                            onClick={() => {
                                                history(
                                                    reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                                                        qs: { id: nitrogenRecommendation.id },
                                                    })
                                                );
                                            }}
                                        >
                                            <Button
                                                variant={isMobile ? 'contained' : 'outlined'}
                                                sx={{ width: { xs: '100%', md: 'auto' } }}
                                            >
                                                <EditOutlinedIcon fontSize='small' sx={{ mr: 1 }} />
                                                {_('Editar')}
                                            </Button>
                                        </Grid>
                                        <Grid item sx={{ width: { xs: '100%', md: 'auto' } }}>
                                            <Button
                                                color='primary'
                                                sx={{ width: { xs: '100%', md: 'auto' } }}
                                                variant={isMobile ? 'outlined' : 'contained'}
                                                onClick={() => {
                                                    mpTrackEvent('N-Smart Recommendation date confirmed', {
                                                        entry_source: 'nitrogen-recommendation-detail',
                                                    });
                                                    updateNSRecommendationFromAPI({
                                                        ...nitrogenRecommendation,
                                                        applicationDate:
                                                            nitrogenRecommendation.estimatedApplicationDate,
                                                    });
                                                    enqueueSnackbar(
                                                        _('La fecha de re-fertilización está confirmada.'),
                                                        { variant: 'success' }
                                                    );
                                                }}
                                            >
                                                {_('Confirmar fecha de re-fertilización ')}
                                                {nitrogenRecommendation.estimatedApplicationDate &&
                                                    formatDate(
                                                        new Date(nitrogenRecommendation.estimatedApplicationDate)
                                                    )}
                                            </Button>
                                        </Grid>
                                    </React.Fragment>
                                )}
                                {nitrogenRecommendation.status === 'available' &&
                                    nitrogenRecommendation.results &&
                                    !!nitrogenRecommendation.results.length && (
                                        <Grid item sx={{ width: { xs: '100%', md: 'auto' } }}>
                                            <Button
                                                color='primary'
                                                variant='contained'
                                                sx={{ width: { xs: '100%', md: 'auto' } }}
                                                onClick={() => {
                                                    nitrogenRecommendation.results.forEach(({ file }, i) => {
                                                        setTimeout(() => {
                                                            const a = document.createElement('a');
                                                            a.setAttribute('href', file);
                                                            a.setAttribute('target', '_blank');
                                                            a.setAttribute(
                                                                'download',
                                                                file.split('?')?.[0]?.split('/').pop()
                                                            );
                                                            a.click();
                                                        }, 100 * i);
                                                    });
                                                }}
                                            >
                                                {_('Descargar informe de Bayer')}
                                                <OpenInNewIcon
                                                    fontSize='small'
                                                    sx={{ ml: 1, display: { xs: 'none', md: 'block' } }}
                                                />
                                            </Button>
                                        </Grid>
                                    )}
                                {nitrogenRecommendation.status === 'processing' && (
                                    <Grid item sx={{ width: { xs: '100%', md: 'auto' } }}>
                                        <Button
                                            variant='contained'
                                            sx={{
                                                bgcolor: '#F5EBC3',
                                                color: 'black',
                                                pointerEvents: 'none',
                                                width: { xs: '100%', md: 'auto' },
                                            }}
                                            disableElevation
                                        >
                                            <LockOutlinedIcon fontSize='small' sx={{ mr: 1 }} />
                                            {_('Estamos preparando la recomendación')}
                                        </Button>
                                    </Grid>
                                )}
                                {!isMobile && (
                                    <Grid item>
                                        <Tooltip title={status.tooltip}>
                                            <Card variant='outlined' sx={{ height: '100%', py: 1, px: 2 }}>
                                                <Title size='sm' bolder sx={{ '& > * + *': { ml: 1 } }}>
                                                    <DotIcon color={status.color} sx={{ verticalAlign: 'middle' }} />
                                                    <span>{status.label}</span>
                                                </Title>
                                            </Card>
                                        </Tooltip>
                                    </Grid>
                                )}
                            </React.Fragment>
                        )}
                    </Grid>
                </Container>
            </Box>
        );
    }
);

export default DetailHeader;
